/**
 * Maps overall scroll progress [0, 1] to per-element visual state for a single key play.
 *
 * Each key play has 5 fixed beats:
 * 1. Header + hero image slam in together
 * 2. Commentary fades in + begins entering from below viewport
 * 3-4. Commentary scrolls upward through full viewport
 * 5. Cinematic zoom-out + blur + rotate exit
 */

/** Fixed beats per play */
const BEATS_PER_PLAY = 5

export interface BeatState {
  active: boolean
  /** Container */
  containerOpacity: number
  containerScale: number
  containerBlur: number
  containerRotate: number
  /** Header */
  headerOpacity: number
  headerTranslateX: number
  headerRotate: number
  headerScale: number
  /** Play number badge */
  badgeScale: number
  badgeRotate: number
  /** Hero image card */
  heroOpacity: number
  heroScale: number
  heroTranslateY: number
  heroRotate: number
  heroBlur: number
  /** Background glow intensity [0, 1] */
  glowIntensity: number
  /** Scroll progress through commentary content [0, 1] */
  commentaryScrollProgress: number
  /** Per-commentary-line state */
  commentaryOpacities: number[]
  commentaryTranslateX: number[]
  commentaryRotates: number[]
  commentaryScales: number[]
}

function makeInactive(commentaryCount: number): BeatState {
  return {
    active: false,
    containerOpacity: 0,
    containerScale: 1,
    containerBlur: 0,
    containerRotate: 0,
    headerOpacity: 0,
    headerTranslateX: -200,
    headerRotate: -12,
    headerScale: 1.4,
    badgeScale: 0,
    badgeRotate: -540,
    heroOpacity: 0,
    heroScale: 0.3,
    heroTranslateY: 150,
    heroRotate: 5,
    heroBlur: 20,
    glowIntensity: 0,
    commentaryScrollProgress: 0,
    commentaryOpacities: Array.from({ length: commentaryCount }, () => 1),
    commentaryTranslateX: Array.from({ length: commentaryCount }, () => 0),
    commentaryRotates: Array.from({ length: commentaryCount }, () => 0),
    commentaryScales: Array.from({ length: commentaryCount }, () => 1),
  }
}

// ─── Easing functions ────────────────────────────────────────────────────

/** Clamp + linear ease */
function clamp01(t: number): number {
  return Math.max(0, Math.min(1, t))
}

/** Overshoots then settles — great for slamming into position */
function easeOutBack(t: number): number {
  const c = clamp01(t)
  const s = 1.70158
  return 1 + (s + 1) * Math.pow(c - 1, 3) + s * Math.pow(c - 1, 2)
}

/** Exponential deceleration — fast start, smooth settle */
function easeOutExpo(t: number): number {
  const c = clamp01(t)
  return c === 1 ? 1 : 1 - Math.pow(2, -10 * c)
}

/** Bouncy elastic — for portraits and badges */
function easeOutElastic(t: number): number {
  const c = clamp01(t)
  if (c === 0 || c === 1) return c
  return (
    Math.pow(2, -10 * c) * Math.sin((c * 10 - 0.75) * ((2 * Math.PI) / 3)) + 1
  )
}

/** Beats per play — fixed at BEATS_PER_PLAY. */
export function beatsForPlay(): number {
  return BEATS_PER_PLAY
}

/**
 * Total beats across all plays.
 */
export function totalBeatsForPlays(commentaryCounts: number[]): number {
  return commentaryCounts.length * BEATS_PER_PLAY
}

/**
 * Returns the beat offset where a given play starts.
 */
function playStartBeat(playIndex: number): number {
  return playIndex * BEATS_PER_PLAY
}

export function computeBeatState(
  progress: number,
  playIndex: number,
  commentaryCounts: number[],
): BeatState {
  const cc = commentaryCounts[playIndex]
  const totalBeats = totalBeatsForPlays(commentaryCounts)
  const startBeat = playStartBeat(playIndex)
  const currentBeat = progress * totalBeats
  const localBeat = currentBeat - startBeat

  if (localBeat < -0.5 || localBeat > BEATS_PER_PLAY + 0.5) {
    return makeInactive(cc)
  }
  if (localBeat < 0) {
    return makeInactive(cc)
  }

  // ─── Beat 1: Header slams in (localBeat 0 → 1) ─────────────────────
  const headerRaw = clamp01(localBeat)
  const headerT = easeOutBack(headerRaw)
  const headerFade = easeOutExpo(headerRaw)

  const headerOpacity = headerFade
  const headerTranslateX = -200 * (1 - headerT)
  const headerRotate = -12 * (1 - headerT)
  const headerScale = 1.4 - 0.4 * headerT

  // Badge spins in with elastic bounce
  const badgeT = easeOutElastic(headerRaw)
  const badgeScale = badgeT
  const badgeRotate = -540 * (1 - clamp01(localBeat))

  // ─── Beat 1 (cont.): Hero launches up alongside header ─────────────
  const heroRaw = clamp01(localBeat)
  const heroT = easeOutBack(heroRaw)
  const heroFade = easeOutExpo(heroRaw)

  const heroOpacity = heroFade
  const heroScale = 0.3 + 0.7 * heroT
  const heroTranslateY = 150 * (1 - heroT)
  const heroRotate = 5 * (1 - heroT)
  const heroBlur = 20 * (1 - heroFade)

  // Glow ramps up as hero appears
  const glowIntensity = clamp01(localBeat - 0.5)

  // ─── Commentary: no per-line animation; scroll position drives reveal ─
  const commentaryOpacities = Array.from({ length: cc }, () => 1)
  const commentaryTranslateX = Array.from({ length: cc }, () => 0)
  const commentaryRotates = Array.from({ length: cc }, () => 0)
  const commentaryScales = Array.from({ length: cc }, () => 1)

  // ─── Commentary scroll: ramps through content over ~2.5 beats ───────
  const commentaryScrollProgress = clamp01((localBeat - 1.5) / 2.5)

  // ─── Last beat: Cinematic zoom-out exit (compressed to half-beat) ───
  const exitBeatStart = BEATS_PER_PLAY - 1
  const exitRaw = clamp01((localBeat - exitBeatStart) * 2)
  const exitT = easeOutExpo(exitRaw)

  const containerOpacity = 1 - exitT
  const containerScale = 1 - 0.2 * exitT
  const containerBlur = 12 * exitT
  const containerRotate = -3 * exitT

  return {
    active: containerOpacity > 0,
    containerOpacity,
    containerScale,
    containerBlur,
    containerRotate,
    headerOpacity: headerOpacity * containerOpacity,
    headerTranslateX,
    headerRotate,
    headerScale,
    badgeScale,
    badgeRotate,
    heroOpacity: heroOpacity * containerOpacity,
    heroScale,
    heroTranslateY,
    heroRotate,
    heroBlur,
    glowIntensity: glowIntensity * containerOpacity,
    commentaryScrollProgress,
    commentaryOpacities: commentaryOpacities.map((o) => o * containerOpacity),
    commentaryTranslateX,
    commentaryRotates,
    commentaryScales,
  }
}

/** Height multiplier per beat, in vh units. */
export const SCROLL_PER_BEAT_VH = 45

export function computeSpacerHeight(commentaryCounts: number[]): string {
  const totalBeats = totalBeatsForPlays(commentaryCounts)
  return `${totalBeats * SCROLL_PER_BEAT_VH}vh`
}
